import { useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import {
  Card,
  Form,
  Input,
  Button,
  Modal,
  Typography,
  Alert,
  Space,
  App,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import DashboardLayout from "./DashboardLayout";

const { Title, Text } = Typography;

export default function RCDelete() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [regNoToDelete, setRegNoToDelete] = useState("");

  const handleDelete = async (values) => {
    const regNo = values.regNo.trim();
    setRegNoToDelete(regNo);
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setConfirmModalOpen(false);

    try {
      const response = await axiosInstance.delete(`/api/advance-rc/${regNoToDelete}`);

      if (response.status === 200) {
        message.success(`RC record ${regNoToDelete} deleted successfully`, 3);
        form.resetFields();
        setRegNoToDelete("");
      }
    } catch (error) {
      console.error("Delete RC error:", error);

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 404) {
          message.error(`RC record ${regNoToDelete} not found in database`, 4);
        } else if (status === 400 || status === 500) {
          const errorMsg =
            data?.error ||
            data?.message ||
            data?.data?.message ||
            `Failed to delete RC record. Status: ${status}`;
          message.error(errorMsg, 4);
        } else {
          message.error(
            `Failed to delete RC record: ${status} ${error.response.statusText}`,
            4
          );
        }
      } else if (error.request) {
        message.error(
          "Connection error. Please check your internet connection and ensure the backend is running.",
          4
        );
      } else {
        message.error(
          error.message || "Failed to delete RC record. Please try again.",
          4
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Delete RC Record">
      <Card
        style={{
          width: "100%",
          maxWidth: "600px",
          margin: "0 auto",
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
          border: "1px solid #e1e8f0",
        }}
      >
        <Space orientation="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <Title
              level={2}
              style={{
                margin: 0,
                background: "linear-gradient(135deg, #ff3366 0%, #cc1a4d 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 700,
              }}
            >
              <DeleteOutlined /> Delete RC Record
            </Title>
          </div>

          <Alert
            title="Warning: Destructive Operation"
            description="This action cannot be undone. Please verify the registration number before deleting."
            type="warning"
            icon={<WarningOutlined />}
            showIcon
            style={{ borderRadius: 8 }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleDelete}
            autoComplete="off"
          >
            <Form.Item
              name="regNo"
              label="Registration Number"
              rules={[
                { required: true, message: "Registration number is required" },
                {
                  min: 3,
                  message: "Registration number must be at least 3 characters",
                },
              ]}
              help="Format: State code + District code + Series + Number (e.g., KL58AD3974)"
            >
              <Input
                placeholder="Enter Registration Number (e.g., KL58AD3974)"
                size="large"
                disabled={loading}
                onPressEnter={handleDelete}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={loading}
                block
                size="large"
                icon={<DeleteOutlined />}
                style={{
                  fontWeight: 600,
                  height: "44px",
                  boxShadow: "0 4px 12px rgba(255, 51, 102, 0.3)",
                }}
              >
                Delete RC Record
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>

      <Modal
        title={
          <span
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#ff3366",
            }}
          >
            Confirm Deletion
          </span>
        }
        open={confirmModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setConfirmModalOpen(false)}
        okText="Delete"
        okButtonProps={{
          danger: true,
          size: "large",
          style: {
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(255, 51, 102, 0.3)",
          },
        }}
        cancelButtonProps={{ size: "large" }}
        style={{ borderRadius: 16 }}
      >
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Text>Are you sure you want to delete RC record:</Text>
          <Text
            strong
            code
            style={{
              fontSize: "16px",
              display: "block",
              textAlign: "center",
              padding: "8px",
              background: "#f0f4ff",
              borderRadius: "8px",
              color: "#0066ff",
            }}
          >
            {regNoToDelete}
          </Text>
          <Alert
            title="This action cannot be undone."
            type="error"
            showIcon
            style={{ borderRadius: 8 }}
          />
        </Space>
      </Modal>
    </DashboardLayout>
  );
}
